/**
 * Calendar-date helpers for the admin play-by-day charts.
 *
 * Dates here are plain 'YYYY-MM-DD' strings naming a day in the EVENT's display
 * timezone (the API buckets them there). They are not instants, so every
 * operation parses and formats them as UTC — going through the browser's local
 * zone would shift an admin in California a day off the event's day boundaries.
 */

const DAY_MS = 86_400_000;

const weekdayFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short' });
const shortDateFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
});

function asDate(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

export function addDays(date: string, days: number): string {
  return new Date(asDate(date).getTime() + days * DAY_MS).toISOString().slice(0, 10);
}

/** Whole days from `from` to `to`; negative when `to` precedes `from`. */
export function daysBetween(from: string, to: string): number {
  return Math.round((asDate(to).getTime() - asDate(from).getTime()) / DAY_MS);
}

/** Inclusive count of days in [from, to]; 0 when the range is empty. */
export function daySpan(from: string, to: string): number {
  return Math.max(0, daysBetween(from, to) + 1);
}

/** Every date from `from` through `to`, inclusive. */
export function dateRange(from: string, to: string): string[] {
  const span = daysBetween(from, to);
  if (span < 0) return [];
  return Array.from({ length: span + 1 }, (_, i) => addDays(from, i));
}

/** 'Fri' */
export function weekdayShort(date: string): string {
  return weekdayFmt.format(asDate(date));
}

/** 'Jul 31' */
export function shortDate(date: string): string {
  return shortDateFmt.format(asDate(date));
}

/** 'Fri Jul 31' */
export function weekdayWithDate(date: string): string {
  return `${weekdayShort(date)} ${shortDate(date)}`;
}

/** 'Jul 31 – Aug 6', collapsing to a single date when the range is one day. */
export function dateRangeLabel(from: string, to: string): string {
  return from === to ? shortDate(from) : `${shortDate(from)} – ${shortDate(to)}`;
}
